import { FC, useMemo, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { useSubscriptionPlans } from '@hooks/api/subscription/useSubscriptionPlans';
import { useLinkedAddOnsForOrganization } from '@hooks/api/subscription/useLinkedAddOnsForOrganization';
import { useAddAddonSubscription } from '@hooks/api/subscription/useAddAddonSubscription';
import { Loading } from '@components/atoms/Loading';
import { BillingPeriod, PlanAddonSummary } from '@models/subscription';
import { PlanUpgradeList } from '@components/molecules/subscription/PlanUpgradeList';

interface AddAddonModalProps {
  isOpen: boolean;
  onClose: () => void;
  noSeatsAvailable?: boolean;
  // Sprint 20, story-093: when both are present, the seat-limit banner shows the exact
  // occupied/included counts instead of the generic sentence — sourced from either the
  // client's own pre-check numbers or the server's NO_SEATS_AVAILABLE error extensions, so
  // both entry points read the same shape.
  occupiedSeats?: number;
  includedSeats?: number;
}

const CONTACT_URL = 'https://getzenhammer.com/contact';

/**
 * Sprint 17, story-082. The one shared Add Seats surface (extended in place, not replaced):
 * - State 1 — the org's current plan has linked add-ons (`getLinkedAddOnsForOrganization`):
 *   purchase cards, priced from the link, unchanged purchase flow.
 * - State 2 — the org's current plan has no linked add-ons: a read-only list of other public
 *   plans that do (from `getSubscriptionPlans`), plus "Contact us to switch plans" — no
 *   self-serve plan change.
 */
export const AddAddonModal: FC<AddAddonModalProps> = ({
  isOpen,
  onClose,
  noSeatsAvailable = false,
  occupiedSeats,
  includedSeats,
}) => {
  const [selectedAddonId, setSelectedAddonId] = useState<string | null>(null);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState<BillingPeriod>(BillingPeriod.MONTHLY);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: linkedAddOns = [], isLoading: isLoadingLinkedAddOns } = useLinkedAddOnsForOrganization({
    enabled: isOpen,
  });
  // Only needed for State 2 (the upgrade list) — still fetched whenever the modal is open since
  // the branch decision itself depends on linkedAddOns, not on this query.
  const { data: publicPlans = [], isLoading: isLoadingPublicPlans } = useSubscriptionPlans({ enabled: isOpen });
  const { mutateAsync: addAddon, isPending: isAddingAddon } = useAddAddonSubscription();

  const isLoading = isLoadingLinkedAddOns || (linkedAddOns.length === 0 && isLoadingPublicPlans);
  const hasLinkedAddOns = linkedAddOns.length > 0;

  const otherPlansWithAddOns = useMemo(
    () =>
      publicPlans
        .filter((plan) => !plan.isAddOn && plan.addOns && plan.addOns.length > 0)
        .map((plan) => ({
          id: plan.id,
          name: plan.name,
          seatPriceCents: Math.min(...(plan.addOns as PlanAddonSummary[]).map((a) => a.priceCents)),
        })),
    [publicPlans],
  );

  const handleAddAddon = async () => {
    if (!selectedAddonId) return;

    try {
      setSubmitError(null);
      await addAddon({ addonId: selectedAddonId, billingPeriod: selectedBillingPeriod });
      onClose();
      setSelectedAddonId(null);
      setSelectedBillingPeriod(BillingPeriod.MONTHLY);
    } catch (error: any) {
      const code = error?.response?.errors?.[0]?.extensions?.code;
      if (code === 'ADDON_NOT_LINKED_TO_PLAN') {
        setSubmitError(
          'This seat package is no longer available on your current plan. Refresh and try again, or contact us if this persists.',
        );
      }
      // Generic errors already surface via useAddAddonSubscription's own toast.
    }
  };

  const handleClose = () => {
    setSelectedAddonId(null);
    setSelectedBillingPeriod(BillingPeriod.MONTHLY);
    setSubmitError(null);
    onClose();
  };

  const formatPrice = (priceCents: number, period: BillingPeriod) => {
    const price = priceCents / 100;
    if (period === BillingPeriod.ANNUAL) {
      return `$${(price * 12).toFixed(2)}/year`;
    }
    return `$${price.toFixed(2)}/month`;
  };

  const selectedAddon = linkedAddOns.find((a) => a.id === selectedAddonId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Seats"
      size="large"
      confirmText={hasLinkedAddOns ? 'Add Seat' : undefined}
      cancelText="Cancel"
      handleConfirm={hasLinkedAddOns ? handleAddAddon : undefined}
      disableConfirm={!selectedAddonId || isAddingAddon}
      isLoading={isAddingAddon}
    >
      {isLoading ? (
        <div className="py-12">
          <Loading center size="lg" />
        </div>
      ) : hasLinkedAddOns ? (
        <div className="space-y-6">
          {noSeatsAvailable && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                  You've reached your seat limit
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  {typeof occupiedSeats === 'number' && typeof includedSeats === 'number'
                    ? `You're using ${occupiedSeats} of ${includedSeats} included seats. Purchase additional seats below to continue.`
                    : 'To add more users to your organization, please purchase additional seats below.'}
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Select a seat package
            </label>
            <div className="grid grid-cols-1 gap-4">
              {linkedAddOns.map((addon: PlanAddonSummary) => {
                const isSelected = selectedAddonId === addon.id;
                const monthlyPrice = addon.priceCents / 100;

                return (
                  <label
                    key={addon.id}
                    onClick={() => setSelectedAddonId(addon.id)}
                    className={`
                      relative flex items-center justify-between p-5 border-2 rounded-lg cursor-pointer transition-all
                      ${isSelected
                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 shadow-md'
                        : 'border-gray-300 dark:border-gray-600 hover:border-indigo-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }
                    `}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {addon.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {addon.includedSeats} additional seat{addon.includedSeats !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      <div className="ml-13 pl-13">
                        <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                          ${monthlyPrice.toFixed(2)}
                          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/month</span>
                        </p>
                      </div>
                    </div>

                    {isSelected && (
                      <div className="absolute top-3 right-3">
                        <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {selectedAddonId && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Billing Period
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label
                  className={`
                    relative flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all
                    ${selectedBillingPeriod === BillingPeriod.MONTHLY
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-indigo-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="billingPeriod"
                    value={BillingPeriod.MONTHLY}
                    checked={selectedBillingPeriod === BillingPeriod.MONTHLY}
                    onChange={() => setSelectedBillingPeriod(BillingPeriod.MONTHLY)}
                    className="sr-only"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">Monthly</p>
                    {selectedAddon && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        ${(selectedAddon.priceCents / 100).toFixed(2)}/month
                      </p>
                    )}
                  </div>
                  {selectedBillingPeriod === BillingPeriod.MONTHLY && (
                    <div className="absolute top-2 right-2">
                      <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </label>

                <label
                  className={`
                    relative flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all
                    ${selectedBillingPeriod === BillingPeriod.ANNUAL
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-indigo-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="billingPeriod"
                    value={BillingPeriod.ANNUAL}
                    checked={selectedBillingPeriod === BillingPeriod.ANNUAL}
                    onChange={() => setSelectedBillingPeriod(BillingPeriod.ANNUAL)}
                    className="sr-only"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">Annual</p>
                    {selectedAddon && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        ${((selectedAddon.priceCents * 12) / 100).toFixed(2)}/year
                      </p>
                    )}
                  </div>
                  {selectedBillingPeriod === BillingPeriod.ANNUAL && (
                    <div className="absolute top-2 right-2">
                      <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </label>
              </div>

              {selectedAddon && (
                <div className="mt-4 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Total</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selectedAddon.includedSeats} additional seat{selectedAddon.includedSeats !== 1 ? 's' : ''} • Billed {selectedBillingPeriod === BillingPeriod.ANNUAL ? 'annually' : 'monthly'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {formatPrice(selectedAddon.priceCents, selectedBillingPeriod)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        charged immediately
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200">{submitError}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Your current plan doesn't offer additional seats.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Plans that include additional seats:
            </p>
            <PlanUpgradeList plans={otherPlansWithAddOns} />
          </div>

          {otherPlansWithAddOns.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Switching plans isn't self-serve today — contact us and we'll take care of it.
            </p>
          )}

          <div className="pt-2">
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium"
            >
              Contact us to switch plans
            </a>
          </div>
        </div>
      )}
    </Modal>
  );
};
